import React from 'react';
import {Link} from 'react-router';
import {Text} from '@albert-io/atomic';

import LoadingIndicator from 'client/generic/LoadingIndicator.react';

import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
import {doesUserBelongToAnyLicensedClassrooms} from 'lib/UserAccessUtil';
import './assets/upgrade-call-to-action.scss';

import questionsImage from 'assets/images/modal-icons/icon1.svg';
import attemptsImage from 'assets/images/modal-icons/icon2.svg';
import explanationImage from 'assets/images/modal-icons/icon3.svg';
import freeResponseImage from 'assets/images/modal-icons/icon4.svg';
import folderImage from 'assets/images/modal-icons/icon5.svg';
import rocketImage from 'assets/images/modal-icons/rocket.svg';

import {hideModal} from '../ModalActions';
import Modal, {ModalContent} from '../Modal';

import upgradeCallToActionStore from './UpgradeCallToActionStore';

interface Props {
  pathname: string;
}

/** Just the subject fields this modal renders — see `selectSubjectCopy`. */
interface SubjectCopy {
  id: string;
  name: string;
  urlSlug: string;
  questionsTotal: number;
  freeResponseTotal: number;
}

interface LicensedClassrooms {
  isReady: boolean;
  value: boolean;
}

/**
 * Selects only the primitives the modal renders, and returns `null` rather than
 * reading the subject at all when the store has none: `getSubject()` falls back to
 * a default model, whose `getMeta()` the class never reached on the loading path.
 */
function selectSubjectCopy(store: typeof upgradeCallToActionStore): SubjectCopy | null {
  if (!store.hasSubjects()) {
    return null;
  }
  const subject = store.getSubject();
  const meta = subject.getMeta();
  return {
    id: subject.getId(),
    name: subject.get('name'),
    urlSlug: subject.getUrlSlug(),
    questionsTotal: meta.getCountOfQuestions(),
    freeResponseTotal: meta.getCountOfFreeResponseQuestions()
  };
}

// Both selectors below build a fresh object per call, and `useSyncExternalStore`
// requires a cached snapshot, so each supplies the comparator that keeps it stable.
function isSameSubjectCopy(a: SubjectCopy | null, b: SubjectCopy | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.urlSlug === b.urlSlug &&
    a.questionsTotal === b.questionsTotal &&
    a.freeResponseTotal === b.freeResponseTotal
  );
}

function isSameLicensedClassrooms(a: LicensedClassrooms, b: LicensedClassrooms): boolean {
  return a.isReady === b.isReady && a.value === b.value;
}

function generateSellingPoint({
  total,
  image,
  content
}: {
  total: number | boolean;
  image: string;
  content: string;
}) {
  if (total === 0) {
    return null;
  }

  return (
    <li className='upgrade-cta__selling-point-wrapper'>
      <img alt='' className='upgrade-cta__image-icon' src={image} />
      <p className='upgrade-cta__selling-point'>{content}</p>
    </li>
  );
}

export default function UpgradeCallToActionModal({pathname}: Props) {
  const isTeacher = useFluxStore(sessionStore, (store) => store.isTeacher());
  const isStudent = useFluxStore(sessionStore, (store) => store.isStudent());
  const licensedClassrooms = useFluxStore(
    sessionStore,
    () => doesUserBelongToAnyLicensedClassrooms(),
    isSameLicensedClassrooms
  );
  const subject = useFluxStore(upgradeCallToActionStore, selectSubjectCopy, isSameSubjectCopy);

  const belongsToLicensedClassrooms = licensedClassrooms.value;

  const hideCTAModal = () => {
    hideModal('UpgradeCallToAction');
  };

  if (subject === null || !licensedClassrooms.isReady) {
    return (
      <Modal modalWrapperClass='modal--upgrade-cta' modalKey='UpgradeCallToAction'>
        <ModalContent>
          <LoadingIndicator />
        </ModalContent>
      </Modal>
    );
  }

  const isLoggedInStudentViewingAssessments =
    isStudent && belongsToLicensedClassrooms && pathname.includes('/assessments');

  const marketplaceLink = `/marketplace/subjects?subject=${subject.urlSlug}&subjectId=${subject.id}`;

  const headerString = belongsToLicensedClassrooms
    ? `You don't have access`
    : 'Upgrade now for full access';

  const callToActionHeader = isTeacher ? (
    <div className='upgrade-cta__header'>
      Take your students to the next level in{' '}
      <span className='upgrade-cta__header-subject'>{subject.name}</span>
    </div>
  ) : (
    <div className='upgrade-cta__header'>
      Take it to the next level for{' '}
      <span className='upgrade-cta__header-subject'>{subject.name}</span>
    </div>
  );

  const generateTagline = () => {
    if (isLoggedInStudentViewingAssessments) {
      return (
        <Text as='p' className='text-center'>
          You can access this assessment in the Assessments tab of your class! If you don&apos;t see
          it there, make sure your teacher has assigned it to you.
        </Text>
      );
    }

    if (isTeacher) {
      return 'With a school license, you get...';
    }

    if (belongsToLicensedClassrooms) {
      return `If you’d like to self-study this subject, you'll need to ask your teacher to grant you access. Once they do, you'll get...`;
    }

    return 'With upgraded access, you get...';
  };

  const generateSellingPoints = () => {
    if (isLoggedInStudentViewingAssessments) {
      return null;
    }
    if (isTeacher) {
      return (
        <ul className='upgrade-cta__selling-point-list'>
          {generateSellingPoint({
            total: subject.questionsTotal,
            image: questionsImage,
            content: `Subscriptions for all of your students with unlimited attempts on all ${subject.questionsTotal} questions`
          })}
          {generateSellingPoint({
            total: true,
            image: freeResponseImage,
            content: `The ability to create assignments for the entire subject`
          })}
          {generateSellingPoint({
            total: true,
            image: folderImage,
            content: `Access to advanced data reporting features`
          })}
        </ul>
      );
    }

    return (
      <ul className='upgrade-cta__selling-point-list'>
        {generateSellingPoint({
          total: subject.questionsTotal,
          image: questionsImage,
          content: `Access to ${subject.questionsTotal} more practice questions`
        })}
        {generateSellingPoint({
          total: subject.freeResponseTotal,
          image: freeResponseImage,
          content: `Access to ${subject.freeResponseTotal} more free response examples`
        })}
        {generateSellingPoint({
          total: true,
          image: explanationImage,
          content: `Detailed explanations for every question`
        })}
        {generateSellingPoint({
          total: true,
          image: attemptsImage,
          content: `Unlimited attempts per question`
        })}
      </ul>
    );
  };

  const generateButtonLink = () => {
    if (isTeacher) {
      return (
        <div className='upgrade-cta__button-wrapper'>
          <a className='button button--green' href='/pricing-schools' onClick={hideCTAModal}>
            Learn about licenses
          </a>
        </div>
      );
    }

    if (belongsToLicensedClassrooms) {
      return null;
    }

    return (
      <div className='upgrade-cta__button-wrapper'>
        <Link className='button button--green' to={marketplaceLink} onClick={hideCTAModal}>
          Upgrade Subject
        </Link>
      </div>
    );
  };

  const generateSubscriptionLink = () => {
    if (!isTeacher) {
      return null;
    }

    return (
      <div className='upgrade-cta__link-wrapper'>
        <Link className='upgrade-cta__link' to={marketplaceLink} onClick={hideCTAModal}>
          Purchase individual subscription
        </Link>
      </div>
    );
  };

  return (
    <Modal
      headerString={headerString}
      modalWrapperClass='modal--upgrade-cta'
      modalKey='UpgradeCallToAction'
    >
      <ModalContent>
        <div className='upgrade-cta'>
          <img alt='' className='upgrade-cta__rocket-img' src={rocketImage} />
          {callToActionHeader}
          <p className='upgrade-cta__header-tagline'>
            <b>{generateTagline()}</b>
          </p>
          {generateSellingPoints()}
          {generateButtonLink()}
          {generateSubscriptionLink()}
        </div>
      </ModalContent>
    </Modal>
  );
}
